import { supabase } from '../supabaseClient';
import { computeNextDueDate } from './recurrence';
import type { Asset, Obligation, Reminder, Completion, Attachment } from '../types';
import { syncObligationToCalendar, deleteObligationFromCalendar } from './googleCalendar';

const ATTACHMENTS_BUCKET = 'nft-attachments';

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

export async function listAssets(): Promise<Asset[]> {
  const { data, error } = await supabase.from('nft_assets').select('*').order('name');
  if (error) throw error;
  return data as Asset[];
}

export async function createAsset(input: Partial<Asset>): Promise<Asset> {
  const { data, error } = await supabase.from('nft_assets').insert(input).select().single();
  if (error) throw error;
  return data as Asset;
}

export async function updateAsset(id: string, input: Partial<Asset>): Promise<Asset> {
  const { data, error } = await supabase
    .from('nft_assets')
    .update(input)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Asset;
}

export async function deleteAsset(id: string): Promise<void> {
  const { error } = await supabase.from('nft_assets').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Obligations
// ---------------------------------------------------------------------------

export async function listObligations(): Promise<Obligation[]> {
  const { data, error } = await supabase
    .from('nft_obligations')
    .select('*')
    .order('due_date');
  if (error) throw error;
  return data as Obligation[];
}

export async function listObligationsForAsset(assetId: string): Promise<Obligation[]> {
  const { data, error } = await supabase
    .from('nft_obligations')
    .select('*')
    .eq('asset_id', assetId)
    .order('due_date');
  if (error) throw error;
  return data as Obligation[];
}

export async function createObligation(
  input: Partial<Obligation>,
  reminderOffsets: number[]
): Promise<Obligation> {
  const { data, error } = await supabase
    .from('nft_obligations')
    .insert(input)
    .select()
    .single();
  if (error) throw error;
  const obligation = data as Obligation;

  if (reminderOffsets.length) {
    await setReminders(obligation.id, reminderOffsets);
  }

  await syncObligationToCalendar(obligation.id).catch((e) =>
    console.warn('Calendar sync skipped:', e.message)
  );

  return obligation;
}

export async function updateObligation(
  id: string,
  input: Partial<Obligation>
): Promise<Obligation> {
  const { data, error } = await supabase
    .from('nft_obligations')
    .update(input)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  const obligation = data as Obligation;

  await syncObligationToCalendar(obligation.id).catch((e) =>
    console.warn('Calendar sync skipped:', e.message)
  );

  return obligation;
}

export async function deleteObligation(id: string): Promise<void> {
  await deleteObligationFromCalendar(id).catch((e) =>
    console.warn('Calendar cleanup skipped:', e.message)
  );
  const { error } = await supabase.from('nft_obligations').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------

export async function listReminders(obligationId: string): Promise<Reminder[]> {
  const { data, error } = await supabase
    .from('nft_reminders')
    .select('*')
    .eq('obligation_id', obligationId)
    .order('offset_days', { ascending: false });
  if (error) throw error;
  return data as Reminder[];
}

export async function listAllReminders(): Promise<Record<string, Reminder[]>> {
  const { data, error } = await supabase.from('nft_reminders').select('*');
  if (error) throw error;
  const byObligation: Record<string, Reminder[]> = {};
  for (const r of data as Reminder[]) {
    (byObligation[r.obligation_id] ??= []).push(r);
  }
  return byObligation;
}

export async function setReminders(obligationId: string, offsets: number[]): Promise<void> {
  await supabase.from('nft_reminders').delete().eq('obligation_id', obligationId);
  if (!offsets.length) return;
  const rows = offsets.map((offset_days) => ({ obligation_id: obligationId, offset_days }));
  const { error } = await supabase.from('nft_reminders').insert(rows);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Completing / snoozing / skipping
// ---------------------------------------------------------------------------

export async function completeObligation(
  obligation: Obligation,
  opts: { completedDate?: string; amount?: number | null; notes?: string | null } = {}
): Promise<Obligation> {
  const completedDate = opts.completedDate ?? new Date().toISOString().slice(0, 10);

  const completion: Partial<Completion> = {
    obligation_id: obligation.id,
    completed_date: completedDate,
    original_due_date: obligation.due_date,
    amount: opts.amount ?? obligation.amount,
    notes: opts.notes ?? null,
    status: 'completed',
  };
  const { error: cErr } = await supabase.from('nft_completions').insert(completion);
  if (cErr) throw cErr;

  const nextDue = computeNextDueDate(
    obligation.due_date,
    obligation.recurrence_type,
    obligation.recurrence_interval
  );

  const update: Partial<Obligation> = {
    last_completed_date: completedDate,
  };
  if (nextDue) {
    update.due_date = nextDue;
  } else {
    // One-time obligation: retire it.
    update.active = false;
  }

  return updateObligation(obligation.id, update);
}

export async function skipObligation(obligation: Obligation): Promise<Obligation> {
  const completion: Partial<Completion> = {
    obligation_id: obligation.id,
    completed_date: new Date().toISOString().slice(0, 10),
    original_due_date: obligation.due_date,
    amount: null,
    notes: null,
    status: 'skipped',
  };
  const { error } = await supabase.from('nft_completions').insert(completion);
  if (error) throw error;

  const nextDue = computeNextDueDate(
    obligation.due_date,
    obligation.recurrence_type,
    obligation.recurrence_interval
  );

  const update: Partial<Obligation> = {};
  if (nextDue) {
    update.due_date = nextDue;
  } else {
    update.active = false;
  }
  return updateObligation(obligation.id, update);
}

export async function snoozeObligation(
  obligation: Obligation,
  newDueDate: string
): Promise<Obligation> {
  return updateObligation(obligation.id, { due_date: newDueDate });
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export async function listCompletions(obligationId: string): Promise<Completion[]> {
  const { data, error } = await supabase
    .from('nft_completions')
    .select('*')
    .eq('obligation_id', obligationId)
    .order('completed_date', { ascending: false });
  if (error) throw error;
  return data as Completion[];
}

// ---------------------------------------------------------------------------
// Attachments (insurance policies, registrations, contracts, receipts...)
// ---------------------------------------------------------------------------

export async function listAttachments(obligationId: string): Promise<Attachment[]> {
  const { data, error } = await supabase
    .from('nft_attachments')
    .select('*')
    .eq('obligation_id', obligationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data as Attachment[];
}

export async function uploadAttachment(
  obligationId: string,
  file: File
): Promise<Attachment> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) throw new Error('Not signed in');
  const ownerId = userData.user.id;

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `${ownerId}/${obligationId}/${Date.now()}_${safeName}`;

  const { error: uploadErr } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .upload(storagePath, file, { contentType: file.type || undefined });
  if (uploadErr) throw uploadErr;

  const { data, error } = await supabase
    .from('nft_attachments')
    .insert({
      obligation_id: obligationId,
      file_name: file.name,
      storage_path: storagePath,
      content_type: file.type || null,
    })
    .select()
    .single();

  if (error) {
    // Clean up the uploaded file if the metadata row failed to save.
    await supabase.storage.from(ATTACHMENTS_BUCKET).remove([storagePath]);
    throw error;
  }

  return data as Attachment;
}

export async function getAttachmentUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, 60 * 10); // 10 minutes
  if (error) throw error;
  return data.signedUrl;
}

export async function deleteAttachment(attachment: Attachment): Promise<void> {
  await supabase.storage.from(ATTACHMENTS_BUCKET).remove([attachment.storage_path]);
  const { error } = await supabase.from('nft_attachments').delete().eq('id', attachment.id);
  if (error) throw error;
}
